import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Lock } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { z } from 'zod'

import { Button } from '@/components/elements/button'
import { Checkbox } from '@/components/elements/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Skeleton } from '@/components/elements/skeleton'
import { TextLink } from '@/components/TextLink'
import TermsAndConditions from '@/content/TermsAndConditions'
import { getStudentCourseContentRecords, startCourse } from '@/services/student_courses.service'
import { RootStoreContext } from '@/stores/RootStore'
import { UserStoreContext } from '@/stores/UserStore'
import { canStartCourseRecord } from '@/utils/certificates'
import { toast } from '@/utils/helpers'

import CertHero from './CertHero'
import CourseStepList from './CourseStepList'

const CourseOverview = observer(({ courseRecord, showCourseModal }) => {
  const { entityId } = useParams()
  const { course } = courseRecord
  const { course: courseContext } = useContext(RootStoreContext)
  const { setCanExit } = courseContext
  const { user } = useContext(UserStoreContext)

  // The waiver only applies to RescueX (Xverse) courses — custom facility/org
  // courses never show it or require acknowledgement. Missing flags fall back
  // to "required" to keep legacy behavior: the backend only computes
  // waiverAcknowledgementRequired when waivers are configured, and a stale
  // course payload without isXverseCourse should still show the waiver rather
  // than skip it.
  const waiverRequired =
    course?.isXverseCourse !== false && user?.waiverAcknowledgementRequired !== false
  const isInProgress = courseRecord.status === 'In Progress'
  // canStart is computed from sibling completion order — keeps URL-paste from
  // letting a student jump ahead of the cert's intended progression. In
  // Progress modules are always startable (we're just resuming).
  const canStart = isInProgress || canStartCourseRecord(courseRecord)

  const siblings = courseRecord.studentCertificate?.courseRecords ?? []
  const currentIndex = siblings.findIndex((r) => r.id === courseRecord.id)

  // Content records only exist after the student clicks Start. Skip the fetch
  // for Not Started — there's nothing to show yet, and the cert hero conveys
  // the "what's ahead" framing on its own.
  const { data: contentRecords, isPending: contentRecordsPending } = useQuery({
    queryKey: ['accounts', 'students', 'courses', courseRecord.id],
    queryFn: async () => {
      // includeQuizAttempts keeps this response shape-identical to
      // CourseModal's fetch — they share a queryKey, so a cache fill from
      // either must satisfy both.
      const response = await getStudentCourseContentRecords(entityId, courseRecord.id, {
        includeQuizAttempts: true,
      })
      return response.results
    },
    enabled: isInProgress,
  })

  const progressStats = useMemo(() => {
    if (!contentRecords || contentRecords.length === 0) return null
    const completed = contentRecords.filter((r) => r.status === 'Completed').length
    return { completed, total: contentRecords.length }
  }, [contentRecords])

  const acknowledgeSchema = z.object({
    acknowledge: z.boolean().refine((val) => val),
  })
  const form = useForm({
    resolver: zodResolver(acknowledgeSchema),
    defaultValues: { acknowledge: !waiverRequired },
  })

  const queryClient = useQueryClient()
  const startCourseMutation = useMutation({
    mutationFn: () => startCourse(entityId, courseRecord.id),
    onError: (err) => toast(err.message, 'error'),
  })

  const handleStart = async () => {
    setCanExit(true)
    if (!isInProgress) {
      await startCourseMutation.mutateAsync()
      queryClient.invalidateQueries({
        queryKey: ['accounts', 'students', 'courses', courseRecord.id],
      })
    }
    showCourseModal()
  }

  return (
    <article className="mx-auto flex w-full max-w-3xl flex-col gap-8">
      <CertHero
        courseRecord={courseRecord}
        entityId={entityId}
        variant="progress"
        stepStats={isInProgress ? progressStats : null}
        // The query is disabled for Not Started, which leaves isPending
        // true forever — only treat In Progress fetches as pending.
        stepStatsPending={isInProgress && contentRecordsPending}
      />

      <section>
        <h2 className="mb-2 text-lg font-semibold">About This Module</h2>
        <p className="text-muted-foreground">{course.description}</p>
      </section>

      {/* Step preview — only meaningful once records exist (In Progress / future). */}
      {isInProgress && (
        <section>
          <h2 className="mb-3 text-lg font-semibold">What You&apos;ll Complete</h2>
          {contentRecordsPending ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <CourseStepList records={contentRecords} />
          )}
        </section>
      )}

      {waiverRequired && !isInProgress && canStart && (
        <section className="border-border rounded-md border p-5">
          <h2 className="mb-2 text-lg font-semibold">Acknowledgements</h2>
          <p className="text-muted-foreground">
            {TermsAndConditions}{' '}
            <TextLink to="/waiver-and-release" target="_blank">
              Read more
            </TextLink>
          </p>
        </section>
      )}

      {!canStart && (
        <section
          aria-label="Module locked"
          className="border-border bg-muted/40 flex flex-col items-center gap-2 rounded-md border border-dashed p-5 text-center"
        >
          <div className="bg-muted rounded-full p-2">
            <Lock className="text-muted-foreground h-5 w-5" aria-hidden="true" />
          </div>
          <p className="text-foreground font-medium">This module is locked</p>
          <p className="text-muted-foreground text-sm">
            Complete the previous {currentIndex === 1 ? 'module' : 'modules'} in this certification
            to unlock it.
          </p>
        </section>
      )}

      {canStart && (
        <form className="flex flex-col items-center" onSubmit={form.handleSubmit(handleStart)}>
          <Form {...form}>
            {waiverRequired && !isInProgress && (
              <FormField
                control={form.control}
                name="acknowledge"
                render={({ field }) => (
                  <FormItem className="mb-4 flex flex-row items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked)}
                      />
                    </FormControl>
                    <FormLabel>I acknowledge the above Terms and Conditions</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <Button
              type="submit"
              size="lg"
              disabled={!form.formState.isValid || startCourseMutation.isPending}
            >
              {isInProgress ? 'Resume module' : 'Start module'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Form>
        </form>
      )}
    </article>
  )
})

export default CourseOverview

CourseOverview.propTypes = {
  courseRecord: PropTypes.object.isRequired,
  showCourseModal: PropTypes.func.isRequired,
}
